import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { TileArgs } from 'react-calendar';
import { Calendar, CalendarProps, Value } from '../../components/Calendar/Calendar';
import { TextInput } from '../../components/Form/TextInput';
import { Icon, close } from '../../components/Icon/Icon';
import { useTranslation } from '../../i18n/i18n';
import './CalendarSelect.css';

interface CalendarSelectProps extends CalendarProps {
	onChangeDate: (value: Value) => void;
	dateValue: string | undefined;
	disabled?: boolean;
	onBlur?: ChangeEventHandler<HTMLDivElement>;
	checkDisabled?: (args: TileArgs) => boolean;
	todayLabel?: string;
	nullable?: boolean;
	minDate?: Date;
	label?: string;
	placeholder?: string;
}

export const CalendarSelect = ({
	onChangeDate,
	minDate,
	maxDate,
	dateValue,
	disabled,
	onBlur,
	checkDisabled,
	todayLabel,
	nullable,
	label,
	placeholder,
}: CalendarSelectProps): JSX.Element => {
	const t = useTranslation().form;
	const [open, setOpen] = useState(false);
	const [touched, setTouched] = useState(false);
	const [changed, setChanged] = useState(false);
	const [position, setPosition] = useState<'bottom' | 'top' | 'hidden'>('hidden');
	const popoverRef = useRef<HTMLDivElement>(null);
	/*
	 * A click on the field decides for itself whether the calendar opens or closes, and the focus
	 * that arrives from the same click must not have an opinion: it would open what that click
	 * just closed, leaving it standing there - invisible, and swallowing the clicks meant for the
	 * controls underneath. Focus still opens it on its own, which is how it opens from a keyboard.
	 *
	 * It lasts to the end of the click and no longer. A field that already has the focus is sent
	 * no focus event at all, and a flag left standing would eat the next one - the way back in.
	 */
	const clickingTheField = useRef(false);
	const classes = ['calendar-select', open && 'open', disabled && 'disabled', changed && 'changed']
		.filter(Boolean)
		.join(' ');

	const isElementAboveViewport = (el: HTMLDivElement): boolean =>
		el.getBoundingClientRect().bottom <= (window.innerHeight || document.documentElement.clientHeight);

	/*
	 * `position` is in the deps, not only `open`: a popover that is mounted but not placed yet is
	 * invisible, and it has to be measured and placed on the very next pass rather than whenever
	 * `open` happens to change again.
	 */
	useEffect(() => {
		if (popoverRef.current && position === 'hidden') {
			if (isElementAboveViewport(popoverRef.current)) {
				setPosition('bottom');
			} else {
				setPosition('top');
			}
		}

		if (open) popoverRef.current && popoverRef.current.focus();

		if (!open) setPosition('hidden');
	}, [open, position]);

	const toggleOpen = (): void => setOpen((prev: boolean) => (disabled ? false : !prev));

	const selectDate = (value: Value): void => {
		if (onChangeDate) onChangeDate(value);

		if (value) {
			toggleOpen();
		}
		setChanged(true);
	};

	const renderDateValue = (): string => {
		if (!dateValue) return '';

		if (todayLabel && new Date(dateValue).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) {
			return todayLabel;
		}

		return dateValue;
	};

	return (
		<>
			<p>{label}</p>
			<div className="calendar-select-container">
				<div
					className={classes}
					onFocus={() => {
						if (!touched) setTouched(true);

						if (clickingTheField.current) return;

						setOpen(true);
					}}
					onBlur={(e) => {
						if (onBlur) onBlur(e);

						/*
						 * One rule for the field and its calendar together: the calendar closes when
						 * the focus leaves both of them. It used to be the popover's own business,
						 * and the popover is focused as it opens - so the focus that the very same
						 * click then gave the field read as leaving, and shut the calendar inside
						 * the click that asked for it. It reappeared when the window came back and
						 * the field was focused again.
						 */
						if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
					}}
				>
					{/* mousedown, because it is the half of the click that comes before the focus */}
					<div
						className="calendar-select-field"
						onMouseDown={() => {
							if (disabled) return;

							clickingTheField.current = true;
							// the focus of this click arrives before the end of the task; nothing later does
							setTimeout(() => (clickingTheField.current = false), 0);

							toggleOpen();
						}}
					>
						<TextInput
							id={label}
							readOnly={true}
							value={renderDateValue()}
							placeholder={placeholder ? placeholder : t.pickDate}
						/>
					</div>
					{open && (
						<div ref={popoverRef} tabIndex={-1} className={`popover ${position}`}>
							<Calendar
								checkDisabled={checkDisabled}
								minDate={minDate}
								maxDate={maxDate}
								onChangeDate={selectDate}
								selectedDate={dateValue}
							/>
						</div>
					)}
				</div>
				{nullable && dateValue && (
					<button className="close-btn" onClick={() => selectDate(null)}>
						<Icon shape={{ ...close, fill: 'black' }} size={20} />
					</button>
				)}
			</div>
		</>
	);
};
